import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import { Loader2, Users, UtensilsCrossed, Plus, Minus, ArrowLeft, ShoppingCart, Copy, Check, MapPin, X, Send, CreditCard, Smartphone, ExternalLink, MessageCircle } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useSeo } from "@/hooks/useSeo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import FloatingSlot from "@/components/FloatingSlot";

/**
 * /basket/:code — public shared food basket. Anyone with the code can view the
 * pooled cart and add items with their own coach + seat. Only the owner can
 * close the basket → one consolidated COD order delivered at the same station.
 */
export default function BasketShare() {
  const { code } = useParams();
  const [params] = useSearchParams();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [payments, setPayments] = useState([]);
  const [err, setErr]   = useState(null);
  const [cart, setCart] = useState({});
  const [name, setName] = useState(() => (typeof window !== "undefined" && localStorage.getItem("basket_name")) || "");
  const [coach, setCoach] = useState("");
  const [seat, setSeat] = useState("");
  const [closing, setClosing] = useState(false);
  const [copied, setCopied] = useState(false);
  const [payModal, setPayModal] = useState(null);   // { coach, seat, amount, member_name }
  useSeo({ title: `Family basket ${code} · find2near`,
           description: "Add your items to the shared train food basket — everyone eats together at the next stop." });

  const load = async () => {
    try {
      const [r1, r2] = await Promise.all([
        api.get(`/trains/food/basket/${code}`),
        api.get(`/trains/food/basket/${code}/payments`),
      ]);
      setData(r1.data); setPayments(r2.data || []); setErr(null);
    } catch (e) { setErr(e?.response?.data?.detail || "Basket unavailable"); }
  };
  useEffect(() => { load(); const id = setInterval(load, 8000); return () => clearInterval(id); /* eslint-disable-next-line */ }, [code]);

  // If Stripe redirected back with ?paid=<session_id>, poll confirm once
  useEffect(() => {
    const sid = params.get("paid");
    if (!sid) return;
    (async () => {
      try {
        const r = await api.post(`/trains/food/basket/${code}/pay/confirm`, { session_id: sid });
        if (r.data?.status === "paid") toast.success("Payment confirmed"); else toast.info("Payment pending — refresh soon");
        load();
      } catch { /* silent */ }
    })();
    // eslint-disable-next-line
  }, []);

  const menu = data?.menu || [];
  const basket = data?.basket || {};
  const isOwner = !!data?.is_owner;
  const isOpen = basket.status === "open";

  const bump = (id, delta) => {
    setCart(c => {
      const next = Math.max(0, (c[id] || 0) + delta);
      const clone = { ...c }; if (next === 0) delete clone[id]; else clone[id] = next; return clone;
    });
  };

  const myTotal = useMemo(() => {
    return menu.reduce((s, m) => s + (cart[m.id] || 0) * m.price, 0);
  }, [menu, cart]);

  const submitMyItems = async () => {
    const items = Object.entries(cart).filter(([, q]) => q > 0);
    if (!items.length)      { toast.error("Add at least one item"); return; }
    if (!name.trim())       { toast.error("Add your name so the family knows who ordered"); return; }
    if (!coach.trim() || !seat.trim()) { toast.error("Enter your coach and seat"); return; }
    localStorage.setItem("basket_name", name.trim());
    try {
      for (const [id, qty] of items) {
        await api.post(`/trains/food/basket/${code}/add`, {
          item_id: id, qty, coach_no: coach, seat, member_name: name.trim(),
        });
      }
      setCart({});
      toast.success("Added to the family basket");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Add failed"); }
  };

  const closeBasket = async () => {
    if (!user)                { toast.error("Sign in as the owner to close"); return; }
    setClosing(true);
    try {
      const r = await api.post(`/trains/food/basket/${code}/close`);
      toast.success(`Order placed for ${r.data.seat_count} seat(s)`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Only the basket owner can close"); }
    finally { setClosing(false); }
  };

  const copyLink = async () => {
    try { await navigator.clipboard.writeText(window.location.href); setCopied(true); setTimeout(() => setCopied(false), 1500); toast.success("Link copied"); }
    catch { /* silent */ }
  };

  if (err) return (
    <div className="min-h-screen bg-slate-950 text-white grid place-items-center p-6">
      <div className="rounded-3xl bg-white/5 border border-white/10 p-6 text-center max-w-sm" data-testid="basket-error">
        <Users size={36} className="mx-auto text-rose-400 mb-2"/>
        <div className="text-lg font-black">Basket not found</div>
        <div className="text-[12px] text-white/60 mt-1">{err}</div>
        <Link to="/live-trains" className="mt-4 inline-block rounded-full bg-emerald-500 text-slate-900 font-black text-[11px] px-4 py-1.5">Open Live Trains</Link>
      </div>
    </div>
  );
  if (!data) return <div className="min-h-screen bg-slate-950 text-white grid place-items-center"><Loader2 className="animate-spin"/></div>;

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-950 via-indigo-950 to-black text-white" data-testid="basket-page">
      <div className="bg-gradient-to-br from-orange-600 to-rose-700 py-8">
        <div className="max-w-2xl mx-auto px-4">
          <Link to="/live-trains" className="text-[11px] text-white/70 hover:text-white inline-flex items-center gap-1"><ArrowLeft size={12}/> Live trains</Link>
          <div className="text-[11px] uppercase tracking-widest font-black opacity-80 flex items-center gap-1 mt-2"><Users size={12}/> Family basket · {code}</div>
          <h1 className="heading text-2xl md:text-4xl font-black mt-1" data-testid="basket-label">{basket.label}</h1>
          <p className="text-sm text-white/80 mt-1"><span className="font-black">{basket.train_name}</span> · Delivery at <span className="font-black text-emerald-200">{basket.station?.name}</span></p>
          <div className="mt-2">
            <Button onClick={copyLink} className="rounded-full bg-white/10 hover:bg-white/20 text-white font-black text-[11px] h-8" data-testid="basket-copy-link">
              {copied ? <><Check size={11} className="mr-1"/> Copied</> : <><Copy size={11} className="mr-1"/> Copy invite link</>}
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-4 space-y-3">
        {/* Current basket state */}
        <div className="rounded-3xl bg-white/5 border border-white/10 p-3 space-y-2" data-testid="basket-state">
          <div className="flex items-center justify-between">
            <div className="text-[10px] uppercase font-black text-white/60 flex items-center gap-1"><ShoppingCart size={11}/> In the basket</div>
            <div className="text-sm font-black text-emerald-300">₹{basket.total || 0}</div>
          </div>
          {basket.by_seat?.length ? basket.by_seat.map((s, i) => {
            const allPaid = s.items.every(it => it.paid);
            return (
              <div key={i} className="rounded-2xl bg-white/5 border border-white/10 p-2" data-testid={`basket-seat-${i}`}>
                <div className="flex items-center justify-between text-[11px]">
                  <span className="font-black text-white">{s.member_name || "Guest"} · Coach {s.coach_no || "—"} · Seat {s.seat || "—"}</span>
                  <span className="font-black text-emerald-300">₹{s.subtotal}</span>
                </div>
                <div className="text-[10px] text-white/60 mt-0.5">
                  {s.items.map((it) => `${it.name} × ${it.qty}`).join(", ")}
                </div>
                <div className="mt-1 flex items-center gap-2">
                  {allPaid ? (
                    <span className="rounded-full bg-emerald-500/20 text-emerald-300 text-[10px] font-black px-2 py-0.5 inline-flex items-center gap-1" data-testid={`basket-seat-paid-${i}`}>
                      <Check size={10}/> Paid
                    </span>
                  ) : (
                    <>
                      <span className="rounded-full bg-amber-500/20 text-amber-300 text-[10px] font-black px-2 py-0.5">Pay ₹{s.subtotal}</span>
                      <Button
                        onClick={() => setPayModal({ coach: s.coach_no, seat: s.seat, amount: s.subtotal, member_name: s.member_name })}
                        className="rounded-full bg-emerald-500 hover:bg-emerald-600 text-slate-900 font-black text-[10px] h-6 px-2"
                        data-testid={`basket-seat-pay-${i}`}>
                        <CreditCard size={10} className="mr-1"/> Pay my share
                      </Button>
                    </>
                  )}
                </div>
              </div>
            );
          }) : <div className="text-[11px] text-white/50">No items yet — invite family to add theirs.</div>}
          <div className="text-[10px] text-white/40">Status: <span className="uppercase font-black">{basket.status}</span> · Refreshes every 8s</div>
        </div>

        {/* Add my items */}
        {isOpen && (
          <div className="rounded-3xl bg-white/5 border border-white/10 p-3 space-y-2" data-testid="basket-add-panel">
            <div className="text-[10px] uppercase font-black text-white/60">Add for your seat</div>
            <div className="grid grid-cols-3 gap-2">
              <Input value={name} onChange={e => setName(e.target.value)} placeholder="Your name"
                     className="rounded-full bg-white/5 border-white/10 text-white text-xs" data-testid="basket-name-input"/>
              <Input value={coach} onChange={e => setCoach(e.target.value.toUpperCase())} placeholder="Coach (A1)"
                     className="rounded-full bg-white/5 border-white/10 text-white text-xs" data-testid="basket-coach-input"/>
              <Input value={seat} onChange={e => setSeat(e.target.value)} placeholder="Seat (34)"
                     className="rounded-full bg-white/5 border-white/10 text-white text-xs" data-testid="basket-seat-input"/>
            </div>
            <div className="space-y-1.5">
              {menu.map(m => (
                <div key={m.id} className="flex items-center gap-2 bg-white/5 rounded-2xl border border-white/10 p-2" data-testid={`basket-item-${m.id}`}>
                  <div className={`w-3 h-3 rounded-sm border ${m.veg ? "border-emerald-400" : "border-rose-400"} flex items-center justify-center`}>
                    <div className={`w-1.5 h-1.5 rounded-full ${m.veg ? "bg-emerald-400" : "bg-rose-400"}`}/>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-black text-sm text-white truncate">{m.name}</div>
                    <div className="text-[10px] text-white/50 truncate">{m.vendor} · ₹{m.price}</div>
                  </div>
                  {cart[m.id] ? (
                    <div className="inline-flex items-center gap-1 bg-emerald-500 rounded-full text-slate-900 font-black text-xs px-1">
                      <button onClick={() => bump(m.id, -1)} data-testid={`basket-item-minus-${m.id}`}><Minus size={12}/></button>
                      <span className="min-w-[16px] text-center">{cart[m.id]}</span>
                      <button onClick={() => bump(m.id, +1)} data-testid={`basket-item-plus-${m.id}`}><Plus size={12}/></button>
                    </div>
                  ) : (
                    <Button onClick={() => bump(m.id, +1)} className="rounded-full bg-emerald-500 text-slate-900 font-black text-[10px] h-7 px-2" data-testid={`basket-item-add-${m.id}`}>
                      <Plus size={11} className="mr-0.5"/> Add
                    </Button>
                  )}
                </div>
              ))}
            </div>
            <div className="flex items-center justify-between pt-2 border-t border-white/10">
              <div className="text-[11px] text-white/60">My subtotal <span className="font-black text-white">₹{myTotal}</span></div>
              <Button disabled={!myTotal} onClick={submitMyItems}
                      className="rounded-full bg-orange-500 hover:bg-orange-600 text-white font-black text-[11px] h-9 px-4" data-testid="basket-submit-btn">
                <Send size={11} className="mr-1"/> Add to basket
              </Button>
            </div>
          </div>
        )}

        {/* Owner close */}
        {isOpen && isOwner && (
          <div className="rounded-2xl bg-emerald-500/10 border border-emerald-400/30 p-3 flex items-center justify-between" data-testid="basket-close-panel">
            <div>
              <div className="text-[10px] uppercase font-black text-emerald-300">Basket owner</div>
              <div className="text-[11px] text-emerald-200">Close and place a single COD order — all parcels arrive together.</div>
            </div>
            <Button disabled={closing || !basket.by_seat?.length} onClick={closeBasket}
                    className="rounded-full bg-emerald-500 hover:bg-emerald-600 text-slate-900 font-black text-[11px] h-9" data-testid="basket-close-btn">
              {closing ? <Loader2 size={12} className="animate-spin"/> : "Close & order"}
            </Button>
          </div>
        )}

        {!isOpen && basket.status === "closed" && (
          <div className="rounded-2xl bg-emerald-500/10 border border-emerald-400/30 p-3" data-testid="basket-closed">
            <div className="text-[10px] uppercase font-black text-emerald-300">Order placed</div>
            <div className="text-[11px] text-emerald-200">All items are on their way. Track it from <Link to="/journey" className="underline">Journey today</Link>.</div>
          </div>
        )}
        {basket.status === "cancelled" && (
          <div className="rounded-2xl bg-rose-500/10 border border-rose-400/30 p-3 text-[11px] text-rose-200" data-testid="basket-cancelled">
            This basket was cancelled by the owner.
          </div>
        )}
      </div>

      {payModal && <BasketPayModal code={code} ctx={payModal} onClose={() => setPayModal(null)} onPaid={() => { setPayModal(null); load(); }}/>}
      {isOpen && <BasketChat code={code} myName={name}/>}
    </div>
  );
}

/**
 * BasketChat — floating bottom-right chat pill; expands to a panel with the
 * running conversation. Poll every 4s using `since=` so the payload stays tiny.
 */
function BasketChat({ code, myName }) {
  const [open, setOpen] = useState(false);
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [name, setName] = useState(() => myName || localStorage.getItem("basket_name") || "");
  const [busy, setBusy] = useState(false);
  const listRef = React.useRef(null);
  const lastRef = React.useRef(null);

  const poll = async () => {
    try {
      const params = lastRef.current ? { since: lastRef.current } : {};
      const r = await api.get(`/trains/food/basket/${code}/chat`, { params });
      if (r.data?.length) {
        setMsgs(prev => {
          const seen = new Set(prev.map(m => m.msg_id));
          const merged = [...prev, ...r.data.filter(m => !seen.has(m.msg_id))];
          return merged.slice(-100);
        });
        lastRef.current = r.data[r.data.length - 1].created_at;
      }
    } catch { /* silent */ }
  };
  useEffect(() => { poll(); const id = setInterval(poll, 4000); return () => clearInterval(id); /* eslint-disable-next-line */ }, [code]);
  useEffect(() => {
    if (open && listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [msgs, open]);

  const send = async () => {
    const m = text.trim();
    const n = (name || "").trim();
    if (!m) return;
    if (!n) { toast.error("Add your name first"); return; }
    setBusy(true);
    try {
      await api.post(`/trains/food/basket/${code}/chat`, { member_name: n, message: m });
      setText("");
      localStorage.setItem("basket_name", n);
      poll();
    } catch (e) { toast.error(e?.response?.data?.detail || "Send failed"); }
    finally { setBusy(false); }
  };

  const unread = !open && msgs.length > 0;

  if (!open) {
    return (
      <FloatingSlot zone="bottom-right" order={2} size="fab" zIndex="fab-secondary" hideOnKeyboard={false}>
        <button onClick={() => setOpen(true)}
                className="rounded-full bg-emerald-500 hover:bg-emerald-600 text-slate-900 font-black text-xs h-12 px-4 shadow-2xl inline-flex items-center gap-2"
                data-testid="basket-chat-toggle">
          <MessageCircle size={14}/> Chat {msgs.length > 0 && <span className="rounded-full bg-slate-900 text-emerald-300 text-[10px] font-black px-1.5">{msgs.length}</span>}
        </button>
      </FloatingSlot>
    );
  }
  return (
    <FloatingSlot zone="bottom-right" order={2} size="fab" zIndex="fab-secondary" hideOnKeyboard={false}>
      <div className="w-80 max-w-[calc(100vw-2rem)] rounded-3xl bg-slate-900 border border-white/10 shadow-2xl flex flex-col overflow-hidden" data-testid="basket-chat-panel">
      <div className="flex items-center justify-between px-3 py-2 border-b border-white/10 bg-white/5">
        <div className="text-[11px] uppercase font-black text-white inline-flex items-center gap-1"><MessageCircle size={12} className="text-emerald-400"/> Basket chat</div>
        <button onClick={() => setOpen(false)} className="text-white/50 hover:text-white" data-testid="basket-chat-close"><X size={13}/></button>
      </div>
      <div ref={listRef} className="p-3 space-y-1.5 max-h-[50vh] min-h-[180px] overflow-y-auto" data-testid="basket-chat-messages">
        {msgs.length === 0 ? (
          <div className="text-[11px] text-white/50 text-center py-6">No messages yet. Say hi 👋</div>
        ) : msgs.map(m => (
          <div key={m.msg_id} className="text-[11px]" data-testid={`chat-msg-${m.msg_id}`}>
            <span className="font-black text-emerald-300">{m.member_name}</span>
            <span className="text-white/40 ml-1 text-[10px]">{new Date(m.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</span>
            <div className="text-white bg-white/5 rounded-xl px-2 py-1 mt-0.5 break-words">{m.message}</div>
          </div>
        ))}
      </div>
      <div className="p-2 border-t border-white/10 space-y-1.5">
        <Input value={name} onChange={e => setName(e.target.value)} placeholder="Your name"
               className="rounded-full bg-white/5 border-white/10 text-white text-xs h-8" data-testid="basket-chat-name"/>
        <div className="flex gap-1.5">
          <Input value={text} onChange={e => setText(e.target.value)}
                 onKeyDown={e => e.key === "Enter" && !e.shiftKey && (e.preventDefault(), send())}
                 placeholder="Type a message…"
                 className="rounded-full bg-white/5 border-white/10 text-white text-xs h-8 flex-1" data-testid="basket-chat-input"/>
          <Button disabled={busy || !text.trim()} onClick={send}
                  className="rounded-full bg-emerald-500 hover:bg-emerald-600 text-slate-900 font-black text-[11px] h-8 px-3" data-testid="basket-chat-send">
            {busy ? <Loader2 size={12} className="animate-spin"/> : <Send size={12}/>}
          </Button>
        </div>
      </div>
      </div>
    </FloatingSlot>
  );
}

function BasketPayModal({ code, ctx, onClose, onPaid }) {
  const [mode, setMode] = useState("upi");
  const [busy, setBusy] = useState(false);
  const [session, setSession] = useState(null);
  const [confirming, setConfirming] = useState(false);

  const startPay = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/trains/food/basket/${code}/pay`, {
        mode,
        member_name: ctx.member_name || "Guest",
        coach_no: ctx.coach, seat: ctx.seat,
        origin_url: window.location.origin,
      });
      setSession(r.data);
      if (mode === "stripe" && r.data.checkout_url) {
        window.location.href = r.data.checkout_url;
      }
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't start payment"); }
    finally { setBusy(false); }
  };

  const confirmUpi = async () => {
    setConfirming(true);
    try {
      const r = await api.post(`/trains/food/basket/${code}/pay/confirm`, { session_id: session.session_id });
      if (r.data.ok) { toast.success("Marked as paid"); onPaid(); }
      else toast.error("Not confirmed yet");
    } catch (e) { toast.error(e?.response?.data?.detail || "Confirm failed"); }
    finally { setConfirming(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/70 grid place-items-center p-4" style={{ zIndex: "var(--z-modal)" }} onClick={onClose} data-testid="basket-pay-modal">
      <div onClick={e => e.stopPropagation()} className="rounded-3xl bg-slate-900 border border-white/10 p-5 w-full max-w-sm space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="font-black text-white text-sm inline-flex items-center gap-1"><CreditCard size={13}/> Pay your share</h3>
          <button onClick={onClose} className="text-white/50 hover:text-white"><X size={14}/></button>
        </div>
        <div className="rounded-2xl bg-white/5 border border-white/10 p-2 text-[11px] text-white/70">
          <div><span className="text-white/50">Seat</span> <span className="font-black text-white">Coach {ctx.coach || "—"} · {ctx.seat || "—"}</span></div>
          <div><span className="text-white/50">Amount</span> <span className="font-black text-emerald-300">₹{ctx.amount}</span></div>
        </div>

        {!session ? (
          <>
            <div className="inline-flex rounded-full bg-white/5 p-1 text-[11px] font-black">
              <button onClick={() => setMode("upi")} className={`rounded-full px-3 py-1 inline-flex items-center gap-1 ${mode === "upi" ? "bg-white text-slate-900" : "text-white/70"}`} data-testid="pay-mode-upi"><Smartphone size={11}/> UPI</button>
              <button onClick={() => setMode("stripe")} className={`rounded-full px-3 py-1 inline-flex items-center gap-1 ${mode === "stripe" ? "bg-white text-slate-900" : "text-white/70"}`} data-testid="pay-mode-stripe"><CreditCard size={11}/> Card</button>
            </div>
            {mode === "upi" ? (
              <div className="text-[11px] text-white/60">
                Tap Pay — we'll open your UPI app (GPay / PhonePe / Paytm) with the amount pre-filled.
              </div>
            ) : (
              <div className="text-[11px] text-white/60">
                You'll be redirected to Stripe Checkout — card, wallet, or bank. Instant confirmation.
              </div>
            )}
            <Button disabled={busy} onClick={startPay}
                    className="rounded-full bg-emerald-500 hover:bg-emerald-600 text-slate-900 font-black text-[11px] w-full" data-testid="pay-start-btn">
              {busy ? <Loader2 size={12} className="animate-spin"/> : `Pay ₹${ctx.amount}`}
            </Button>
          </>
        ) : session.mode === "upi" ? (
          <>
            <div className="rounded-2xl bg-sky-500/10 border border-sky-400/30 p-3 space-y-2">
              <div className="text-[10px] uppercase font-black text-sky-300">UPI intent ready</div>
              <a href={session.upi_intent}
                 className="block text-center rounded-full bg-emerald-500 hover:bg-emerald-600 text-slate-900 font-black text-[11px] py-2"
                 data-testid="pay-upi-open">
                <ExternalLink size={11} className="inline mr-1"/> Open my UPI app
              </a>
              <div className="text-[10px] text-white/60 break-all font-mono">{session.upi_intent}</div>
            </div>
            <Button disabled={confirming} onClick={confirmUpi}
                    className="rounded-full bg-emerald-500 hover:bg-emerald-600 text-slate-900 font-black text-[11px] w-full" data-testid="pay-upi-confirm">
              {confirming ? <Loader2 size={12} className="animate-spin"/> : "I've paid — mark as done"}
            </Button>
          </>
        ) : (
          <div className="text-[11px] text-white/70">Redirecting to Stripe…</div>
        )}
      </div>
    </div>
  );
}
